/**
 * Stateless HMAC-signed OAuth refresh tokens.
 *
 * Why this exists (TEN-1661, DEC-783):
 * Railway redeploys on every git push, wiping in-memory Maps. The previous
 * `refreshTokens` Map made claude.ai's proactive refresh calls fail with
 * `invalid_grant`, forcing OAuth re-auth after every deploy. DEC-783 fixed
 * the access-token side by returning the underlying pb_sk_* key directly.
 * This module is the refresh-token equivalent: a self-contained,
 * HMAC-signed token format that needs no server-side state.
 *
 * Format: pb_rt_<base64url(payload)>.<base64url(hmac-sha256(payload))>
 * Payload: { k: apiKey, i: iat-ms, j: jti }
 *
 * The jti (j) field is reserved for future revocation/rotation work and is
 * not yet checked. Signature comparison uses crypto.timingSafeEqual.
 */

import { createHmac, randomBytes, randomUUID, timingSafeEqual } from "node:crypto";

export const REFRESH_TOKEN_TTL_MS = 90 * 24 * 60 * 60_000; // 90 days
const PREFIX = "pb_rt_";

interface RefreshPayload {
  k: string; // apiKey
  i: number; // iat (ms since epoch)
  j: string; // jti
}

// Read once at module load. In production we warn rather than crash so the
// server keeps working — degrades gracefully to current Railway-lifetime
// behaviour if the secret is missing.
const secret: Buffer = (() => {
  const fromEnv = process.env.MCP_REFRESH_SECRET;
  if (fromEnv && fromEnv.length > 0) return Buffer.from(fromEnv, "utf8");
  if (process.env.NODE_ENV === "production") {
    // eslint-disable-next-line no-console
    console.warn(
      "[HTTP] WARNING MCP_REFRESH_SECRET not set — refresh tokens will not survive restart",
    );
  }
  return randomBytes(32);
})();

function sign(payloadB64: string): Buffer {
  return createHmac("sha256", secret).update(payloadB64).digest();
}

export function signRefreshToken(apiKey: string): string {
  const payload: RefreshPayload = {
    k: apiKey,
    i: Date.now(),
    j: randomUUID(),
  };
  const payloadB64 = Buffer.from(JSON.stringify(payload), "utf8").toString("base64url");
  const sigB64 = sign(payloadB64).toString("base64url");
  return `${PREFIX}${payloadB64}.${sigB64}`;
}

export function verifyRefreshToken(token: string): { apiKey: string } | null {
  if (typeof token !== "string" || !token.startsWith(PREFIX)) return null;
  const body = token.slice(PREFIX.length);
  const dot = body.indexOf(".");
  if (dot <= 0 || dot === body.length - 1) return null;
  const payloadB64 = body.slice(0, dot);
  const sigB64 = body.slice(dot + 1);

  let providedSig: Buffer;
  try {
    providedSig = Buffer.from(sigB64, "base64url");
  } catch {
    return null;
  }
  const expectedSig = sign(payloadB64);
  if (providedSig.length !== expectedSig.length) return null;
  if (!timingSafeEqual(providedSig, expectedSig)) return null;

  let payload: RefreshPayload;
  try {
    const json = Buffer.from(payloadB64, "base64url").toString("utf8");
    payload = JSON.parse(json);
  } catch {
    return null;
  }
  if (
    !payload ||
    typeof payload.k !== "string" ||
    typeof payload.i !== "number" ||
    typeof payload.j !== "string"
  ) {
    return null;
  }
  if (Date.now() - payload.i > REFRESH_TOKEN_TTL_MS) return null;

  return { apiKey: payload.k };
}
